import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Mail, CreditCard, Crown, Key, Search, Trash2, Send, RefreshCw, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import useAuthStore from '../store/useAuthStore';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge(props) {
  var status = props.status;
  var memberspace = props.memberspace;
  var label = status;
  var classes = 'px-2 py-0.5 rounded-full text-xs font-medium ';

  if (memberspace) {
    label = 'MemberSpace';
    classes += 'bg-purple-100 text-purple-700';
  } else if (status === 'active' || status === 'trialing') {
    label = 'Stripe';
    classes += 'bg-emerald-100 text-emerald-700';
  } else if (status === 'free_code') {
    label = 'Access Code';
    classes += 'bg-blue-100 text-blue-700';
  } else if (status === 'canceled' || status === 'canceled_memberspace') {
    label = 'Canceled';
    classes += 'bg-rose-100 text-rose-700';
  } else {
    label = 'No Access';
    classes += 'bg-gray-100 text-gray-600';
  }

  return <span className={classes}>{label}</span>;
}

function AdminDashboard() {
  var navigate = useNavigate();
  var token = useAuthStore().token;
  var currentUser = useAuthStore().user;
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  var [activeTab, setActiveTab] = useState('users');
  var [selectedUsers, setSelectedUsers] = useState({});
  var [selectedLeads, setSelectedLeads] = useState({});
  var [deleting, setDeleting] = useState(false);
  var [emailing, setEmailing] = useState(false);
  var [emailResult, setEmailResult] = useState(null);
  var [showCredentials, setShowCredentials] = useState(null);
  var [showSync, setShowSync] = useState(false);
  var [syncEmails, setSyncEmails] = useState('');
  var [syncing, setSyncing] = useState(false);
  var [syncResult, setSyncResult] = useState(null);
  var [traffic, setTraffic] = useState(null);

  function fetchData() {
    if (!token) return;
    fetch(BACKEND_URL + '/api/admin/dashboard', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(function(r) { return r.json(); })
      .then(function(d) { setData(d); setSelectedUsers({}); setSelectedLeads({}); })
      .catch(console.error)
      .finally(function() { setLoading(false); });
    fetch(BACKEND_URL + '/api/admin/traffic-sources', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(function(r) { return r.json(); })
      .then(setTraffic)
      .catch(console.error);
  }

  useEffect(fetchData, [token]);

  function formatDate(iso) {
    if (!iso) return '—';
    var d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function toggleUserSelect(id) {
    setSelectedUsers(function(prev) {
      var next = Object.assign({}, prev);
      if (next[id]) { delete next[id]; } else { next[id] = true; }
      return next;
    });
  }

  function toggleLeadSelect(email) {
    setSelectedLeads(function(prev) {
      var next = Object.assign({}, prev);
      if (next[email]) { delete next[email]; } else { next[email] = true; }
      return next;
    });
  }

  function toggleAllUsers(filtered) {
    var allSelected = filtered.length > 0 && filtered.every(function(u) { return selectedUsers[u.id]; });
    if (allSelected) {
      setSelectedUsers({});
    } else {
      var next = {};
      filtered.forEach(function(u) {
        if (u.email !== currentUser.email) { next[u.id] = true; }
      });
      setSelectedUsers(next);
    }
  }

  function toggleAllLeads(filtered) {
    var allSelected = filtered.length > 0 && filtered.every(function(l) { return selectedLeads[l.email]; });
    if (allSelected) {
      setSelectedLeads({});
    } else {
      var next = {};
      filtered.forEach(function(l) { next[l.email] = true; });
      setSelectedLeads(next);
    }
  }

  function deleteSelectedUsers() {
    var ids = Object.keys(selectedUsers);
    if (ids.length === 0) return;
    if (!window.confirm('Delete ' + ids.length + ' user(s)? This cannot be undone.')) return;
    setDeleting(true);
    fetch(BACKEND_URL + '/api/admin/users/bulk-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ ids: ids })
    }).then(function() { fetchData(); }).finally(function() { setDeleting(false); });
  }

  function deleteSelectedLeads() {
    var emails = Object.keys(selectedLeads);
    if (emails.length === 0) return;
    if (!window.confirm('Delete ' + emails.length + ' lead(s)? This cannot be undone.')) return;
    setDeleting(true);
    fetch(BACKEND_URL + '/api/admin/leads/bulk-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ ids: emails })
    }).then(function() { fetchData(); }).finally(function() { setDeleting(false); });
  }

  function deleteSingleUser(id) {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    setDeleting(true);
    fetch(BACKEND_URL + '/api/admin/users/' + id, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() { fetchData(); }).finally(function() { setDeleting(false); });
  }

  function emailCredentials() {
    if (!window.confirm('This will reset passwords for all MemberSpace members and sync their credentials to MailerLite. You can then send the campaign from your MailerLite dashboard. Continue?')) return;
    setEmailing(true);
    setEmailResult(null);
    setShowCredentials(null);
    fetch(BACKEND_URL + '/api/admin/email-credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
    })
      .then(function(r) {
        if (!r.ok) {
          return r.text().then(function(t) { throw new Error(t.slice(0, 200)); });
        }
        return r.json();
      })
      .then(function(d) {
        if (d.success) {
          setEmailResult({
            type: 'success',
            message: d.synced + ' member(s) synced to MailerLite group "MemberSpace Members" with new credentials.' + (d.failed && d.failed.length > 0 ? ' Failed: ' + d.failed.join(', ') : '')
          });
          setShowCredentials(d.credentials || []);
        } else {
          setEmailResult({ type: 'error', message: d.error || d.detail || 'Sync failed' });
        }
      })
      .catch(function(e) { setEmailResult({ type: 'error', message: 'Request failed: ' + e.message }); })
      .finally(function() { setEmailing(false); });
  }

  function downloadCredentialsCSV() {
    if (!showCredentials || showCredentials.length === 0) return;
    var lines = ['email,name,temp_password'];
    for (var i = 0; i < showCredentials.length; i++) {
      var m = showCredentials[i];
      lines.push(m.email + ',' + (m.name || '') + ',' + m.temp_password);
    }
    var blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    var link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'member_credentials.csv';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function syncMembers() {
    var lines = syncEmails.split('\n').map(function(l) { return l.trim(); }).filter(function(l) { return l && l.includes('@'); });
    if (lines.length === 0) { window.alert('Please paste at least one email address'); return; }
    if (!window.confirm('This will sync ' + lines.length + ' email(s). Members NOT on this list will have access REVOKED. Continue?')) return;
    setSyncing(true);
    setSyncResult(null);
    fetch(BACKEND_URL + '/api/admin/sync-members', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ emails: lines })
    })
      .then(function(r) {
        if (!r.ok) return r.text().then(function(t) { throw new Error(t.slice(0, 200)); });
        return r.json();
      })
      .then(function(d) {
        setSyncResult(d);
        fetchData();
      })
      .catch(function(e) { setSyncResult({ success: false, error: e.message }); })
      .finally(function() { setSyncing(false); });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Failed to load dashboard</div>
      </div>
    );
  }

  var stats = data.stats;
  var trafficRows = (traffic && traffic.sources) || [];
  var searchLower = searchTerm.toLowerCase();

  var filteredUsers = data.users.filter(function(u) {
    if (!searchTerm) return true;
    return (u.email && u.email.toLowerCase().indexOf(searchLower) !== -1) ||
           (u.name && u.name.toLowerCase().indexOf(searchLower) !== -1);
  });

  var filteredLeads = data.leads.filter(function(l) {
    if (!searchTerm) return true;
    return (l.email && l.email.toLowerCase().indexOf(searchLower) !== -1) ||
           (l.name && l.name.toLowerCase().indexOf(searchLower) !== -1);
  });

  var selectedUserCount = Object.keys(selectedUsers).length;
  var selectedLeadCount = Object.keys(selectedLeads).length;
  var allUsersSelected = filteredUsers.length > 0 && filteredUsers.filter(function(u) { return u.email !== currentUser.email; }).every(function(u) { return selectedUsers[u.id]; });
  var allLeadsSelected = filteredLeads.length > 0 && filteredLeads.every(function(l) { return selectedLeads[l.email]; });

  return (
    <div className="min-h-screen noise-bg" data-testid="admin-dashboard-page">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={function() { navigate('/'); }} className="btn-press" data-testid="dashboard-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={function() { setShowSync(!showSync); setSyncResult(null); }} data-testid="sync-members-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync Members
            </Button>
            <Button variant="outline" size="sm" onClick={emailCredentials} disabled={emailing || deleting} data-testid="email-credentials-btn">
              <Send className="w-4 h-4 mr-2" />
              {emailing ? 'Syncing...' : 'Sync & Email Credentials'}
            </Button>
            <Button variant="outline" size="sm" onClick={function() { navigate('/admin/codes'); }} data-testid="manage-codes-btn">
              <Key className="w-4 h-4 mr-2" />
              Access Codes
            </Button>
            <Button variant="outline" size="sm" onClick={function() { navigate('/admin/bulk-import'); }} data-testid="bulk-import-btn">
              <Users className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-8 space-y-8">
        {showSync && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border-2 border-border rounded-2xl brutalist-shadow p-6 space-y-4" data-testid="sync-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Sync Skool Members
              </h3>
              <button onClick={function() { setShowSync(false); }} className="text-muted-foreground hover:text-foreground">&times;</button>
            </div>
            <p className="text-sm text-muted-foreground">
              Paste your current Skool member emails below (one per line). Members on this list will get access. Members NOT on this list will have access <strong className="text-rose-500">revoked</strong>.
            </p>
            <textarea
              value={syncEmails}
              onChange={function(e) { setSyncEmails(e.target.value); }}
              placeholder={"member1@email.com\nmember2@email.com\nmember3@email.com"}
              rows={8}
              className="w-full rounded-xl border-2 border-border bg-secondary/50 p-3 text-sm font-mono focus:outline-none focus:border-accent resize-y"
              data-testid="sync-emails-textarea"
            />
            <div className="flex items-center gap-3">
              <Button onClick={syncMembers} disabled={syncing || !syncEmails.trim()} className="bg-accent hover:bg-accent/90" data-testid="sync-submit-btn">
                <RefreshCw className={'w-4 h-4 mr-2' + (syncing ? ' animate-spin' : '')} />
                {syncing ? 'Syncing...' : 'Sync Member List'}
              </Button>
              <span className="text-xs text-muted-foreground">{syncEmails.split('\n').filter(function(l) { return l.trim() && l.includes('@'); }).length} email(s)</span>
            </div>

            {syncResult && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={'rounded-xl p-4 text-sm ' + (syncResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200')} data-testid="sync-result">
                {syncResult.success ? (
                  <div className="space-y-2">
                    <p className="font-semibold text-emerald-800">Sync complete!</p>
                    <p className="text-emerald-700">Added: <strong>{syncResult.added}</strong> | Revoked: <strong className="text-rose-600">{syncResult.revoked}</strong> | Unchanged: <strong>{syncResult.unchanged}</strong></p>
                  </div>
                ) : (
                  <p className="text-rose-700">{syncResult.error || 'Sync failed'}</p>
                )}
              </motion.div>
            )}
          </motion.div>
        )}

        {emailResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={'rounded-xl px-4 py-3 text-sm font-medium flex items-center justify-between ' + (emailResult.type === 'success' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-rose-100 text-rose-800 border border-rose-200')}
            data-testid="email-result-banner"
          >
            <span>{emailResult.message}</span>
            <button onClick={function() { setEmailResult(null); setShowCredentials(null); }} className="ml-4 text-current opacity-60 hover:opacity-100">&times;</button>
          </motion.div>
        )}

        {showCredentials && showCredentials.length > 0 && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border-2 border-border rounded-2xl brutalist-shadow p-6 space-y-4" data-testid="credentials-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Credentials Synced to MailerLite</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={downloadCredentialsCSV} data-testid="download-csv-btn">
                  Download CSV
                </Button>
                <Button variant="default" size="sm" onClick={function() { window.open('https://dashboard.mailerlite.com/campaigns/create', '_blank'); }} data-testid="open-mailerlite-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Create Campaign in MailerLite
                </Button>
              </div>
            </div>

            <div className="bg-secondary/50 rounded-xl p-4 text-sm space-y-2" data-testid="mailerlite-instructions">
              <p className="font-semibold">How to send the email from MailerLite:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Click "Create Campaign in MailerLite" above</li>
                <li>Choose "Regular campaign" and name it</li>
                <li>Set recipients to the <strong>"MemberSpace Members"</strong> group</li>
                <li>Design your email — use these personalization variables:</li>
              </ol>
              <div className="bg-card rounded-lg p-3 font-mono text-xs space-y-1 mt-2">
                <p><span className="text-accent font-bold">{'{'+'$email'+'}'}</span> — Member's login email</p>
                <p><span className="text-accent font-bold">{'{'+'$temp_password'+'}'}</span> — Their new temporary password</p>
                <p><span className="text-accent font-bold">{'{'+'$name'+'}'}</span> — Member's name</p>
              </div>
              <p className="text-muted-foreground mt-2">Each member's password is stored as a custom field, so MailerLite will personalize each email automatically!</p>
            </div>

            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-sm" data-testid="credentials-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Temp Password</th>
                  </tr>
                </thead>
                <tbody>
                  {showCredentials.map(function(c, i) {
                    return (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-2 px-3 font-mono text-xs">{c.email}</td>
                        <td className="py-2 px-3">{c.name || '—'}</td>
                        <td className="py-2 px-3 font-mono text-xs">{c.temp_password}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border-2 border-border rounded-2xl p-4 brutalist-shadow" data-testid="stat-total-users">
            <Users className="w-4 h-4 text-muted-foreground mb-1" />
            <div className="text-2xl font-bold font-mono">{stats.total_users}</div>
            <div className="text-xs text-muted-foreground">Total Users</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-card border-2 border-border rounded-2xl p-4 brutalist-shadow" data-testid="stat-active-users">
            <Users className="w-4 h-4 text-emerald-500 mb-1" />
            <div className="text-2xl font-bold font-mono">{stats.active_users}</div>
            <div className="text-xs text-muted-foreground">Active Access</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card border-2 border-border rounded-2xl p-4 brutalist-shadow" data-testid="stat-stripe">
            <CreditCard className="w-4 h-4 text-accent mb-1" />
            <div className="text-2xl font-bold font-mono">{stats.stripe_subscribers}</div>
            <div className="text-xs text-muted-foreground">Stripe Subs</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-card border-2 border-border rounded-2xl p-4 brutalist-shadow" data-testid="stat-memberspace">
            <Crown className="w-4 h-4 text-purple-500 mb-1" />
            <div className="text-2xl font-bold font-mono">{stats.memberspace_members}</div>
            <div className="text-xs text-muted-foreground">MemberSpace</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card border-2 border-border rounded-2xl p-4 brutalist-shadow" data-testid="stat-code-users">
            <Key className="w-4 h-4 text-blue-500 mb-1" />
            <div className="text-2xl font-bold font-mono">{stats.code_users}</div>
            <div className="text-xs text-muted-foreground">Access Codes</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-card border-2 border-border rounded-2xl p-4 brutalist-shadow" data-testid="stat-leads">
            <Mail className="w-4 h-4 text-accent mb-1" />
            <div className="text-2xl font-bold font-mono">{stats.total_leads}</div>
            <div className="text-xs text-muted-foreground">Email Leads</div>
          </motion.div>
        </div>

        {trafficRows.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card border-2 border-border rounded-2xl brutalist-shadow overflow-hidden" data-testid="traffic-sources-panel">
            <div className="flex items-center gap-2 p-4 border-b border-border">
              <TrendingUp className="w-4 h-4 text-accent" />
              <h3 className="font-semibold">Traffic Sources</h3>
              <span className="text-xs text-muted-foreground ml-auto">Use links like learnmynumbers.com/?src=youtube in your video descriptions</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="traffic-sources-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 text-left">
                    <th className="p-3 font-medium">Source</th>
                    <th className="p-3 font-medium">Visits (30d)</th>
                    <th className="p-3 font-medium">Visits (All)</th>
                    <th className="p-3 font-medium">Email Leads</th>
                    <th className="p-3 font-medium">Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {trafficRows.map(function(row, i) {
                    return (
                      <tr key={row.source} className="border-b border-border/50 hover:bg-secondary/20 transition-colors" data-testid={'traffic-row-' + i}>
                        <td className="p-3 font-mono font-medium capitalize">{row.source}</td>
                        <td className="p-3 font-mono">{row.visits_30d}</td>
                        <td className="p-3 font-mono">{row.visits}</td>
                        <td className="p-3 font-mono">{row.leads}</td>
                        <td className="p-3 font-mono">{row.signups}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <Tabs value={activeTab} onValueChange={function(v) { setActiveTab(v); setSelectedUsers({}); setSelectedLeads({}); }}>
            <TabsList className="bg-secondary">
              <TabsTrigger value="users" className="data-[state=active]:bg-accent data-[state=active]:text-white" data-testid="tab-users">
                Users ({data.users.length})
              </TabsTrigger>
              <TabsTrigger value="leads" className="data-[state=active]:bg-accent data-[state=active]:text-white" data-testid="tab-leads">
                Leads ({data.leads.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          {activeTab === 'users' && selectedUserCount > 0 && (
            <Button variant="destructive" size="sm" onClick={deleteSelectedUsers} disabled={deleting} data-testid="bulk-delete-users-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedUserCount} User{selectedUserCount > 1 ? 's' : ''}
            </Button>
          )}
          {activeTab === 'leads' && selectedLeadCount > 0 && (
            <Button variant="destructive" size="sm" onClick={deleteSelectedLeads} disabled={deleting} data-testid="bulk-delete-leads-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedLeadCount} Lead{selectedLeadCount > 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {activeTab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card border-2 border-border rounded-2xl brutalist-shadow overflow-hidden" data-testid="users-table-container">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="py-3 px-3 w-10">
                      <input type="checkbox" checked={allUsersSelected} onChange={function() { toggleAllUsers(filteredUsers); }} className="rounded" data-testid="select-all-users" />
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Access</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Joined</th>
                    <th className="py-3 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(function(u, i) {
                    var isSelf = currentUser && u.email === currentUser.email;
                    return (
                      <tr key={u.id || i} className={'border-b border-border/50 hover:bg-secondary/20 transition-colors' + (selectedUsers[u.id] ? ' bg-accent/5' : '')} data-testid={'user-row-' + i}>
                        <td className="py-3 px-3">
                          {!isSelf && (
                            <input type="checkbox" checked={!!selectedUsers[u.id]} onChange={function() { toggleUserSelect(u.id); }} className="rounded" data-testid={'select-user-' + i} />
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">{u.email}</td>
                        <td className="py-3 px-4">{u.name || '—'}</td>
                        <td className="py-3 px-4">
                          <StatusBadge status={u.subscription_status} memberspace={u.memberspace_member} />
                        </td>
                        <td className="py-3 px-4">
                          {u.has_access ? (
                            <span className="text-emerald-500 font-medium">Active</span>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">{formatDate(u.created_at)}</td>
                        <td className="py-3 px-3">
                          {!isSelf && (
                            <button onClick={function() { deleteSingleUser(u.id); }} className="text-muted-foreground hover:text-rose-500 transition-colors" disabled={deleting} data-testid={'delete-user-' + i}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-muted-foreground">No users found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {activeTab === 'leads' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card border-2 border-border rounded-2xl brutalist-shadow overflow-hidden" data-testid="leads-table-container">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="py-3 px-3 w-10">
                      <input type="checkbox" checked={allLeadsSelected} onChange={function() { toggleAllLeads(filteredLeads); }} className="rounded" data-testid="select-all-leads" />
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Source</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Captured</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map(function(l, i) {
                    return (
                      <tr key={i} className={'border-b border-border/50 hover:bg-secondary/20 transition-colors' + (selectedLeads[l.email] ? ' bg-accent/5' : '')} data-testid={'lead-row-' + i}>
                        <td className="py-3 px-3">
                          <input type="checkbox" checked={!!selectedLeads[l.email]} onChange={function() { toggleLeadSelect(l.email); }} className="rounded" data-testid={'select-lead-' + i} />
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">{l.email}</td>
                        <td className="py-3 px-4">{l.name || '—'}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">{l.source || 'unknown'}</span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">{formatDate(l.created_at)}</td>
                      </tr>
                    );
                  })}
                  {filteredLeads.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-muted-foreground">No leads found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;
